import { apiClient } from '../client';
import type { CsTicketStatus, CsTier, CsTriageDecision } from './tickets.api';

/**
 * Client for the CS Operations surfaces (Phase 16 / ADR-026):
 *
 *   - `/api/cs/operations/health`          — health-check runs + run-now
 *   - `/api/cs/operations/credentials`     — rotation policies + events
 *   - `/api/cs/operations/security-policy` — org security policy
 *   - `/api/cs/operations/errors`          — integration error log
 *   - `/api/cs/triage`                     — triage decisions + backlog run
 *   - `/api/cs/ops-agent`                  — read-only ops agent runs
 *
 * Mirrors server/src/routes/cs/{health,credentials,security,errors,
 * triage,ops-agent}.routes.ts and their services.
 */

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
}

// ── Health checks ───────────────────────────────────────────────────────

export interface CsHealthCheck {
  name: string;
  ok: boolean;
  detail: string;
}

export interface CsHealthCheckRun {
  id: string;
  organizationId: string;
  triggeredBy: 'cron' | 'manual';
  triggeredByMembershipId: string | null;
  resultsJson: { ok: boolean; checks: CsHealthCheck[] };
  runAt: string;
}

export async function getLatestHealthRun(): Promise<CsHealthCheckRun | null> {
  const res = await apiClient.get<{ data: CsHealthCheckRun | null }>('/api/cs/operations/health/latest');
  return res.data.data;
}

export async function listHealthRuns(
  page = 1,
  limit = 20,
): Promise<{ data: CsHealthCheckRun[]; meta: PageMeta }> {
  const res = await apiClient.get<{ data: CsHealthCheckRun[]; meta: PageMeta }>(
    `/api/cs/operations/health/runs?page=${String(page)}&limit=${String(limit)}`,
  );
  return res.data;
}

export async function runHealthCheckNow(): Promise<CsHealthCheckRun> {
  const res = await apiClient.post<{ data: CsHealthCheckRun }>('/api/cs/operations/health/run-now', {});
  return res.data.data;
}

// ── Credentials ─────────────────────────────────────────────────────────

export type CredentialPolicyStatus = 'ok' | 'due_soon' | 'overdue' | 'never_rotated';

export interface CredentialPolicyView {
  id: string;
  provider: string;
  rotationPolicyDays: number;
  lastRotatedAt: string | null;
  managedByHub: boolean;
  dueAt: string | null;
  status: CredentialPolicyStatus;
}

export async function listCredentialPolicies(): Promise<CredentialPolicyView[]> {
  const res = await apiClient.get<{ data: CredentialPolicyView[] }>('/api/cs/operations/credentials');
  return res.data.data;
}

export type CsRotationOutcome = 'rotated' | 'test_failed' | 'marked_manual';

export interface CsCredentialRotationEvent {
  id: string;
  organizationId: string;
  provider: string;
  integrationId: string | null;
  rotatedByMembershipId: string | null;
  fromFingerprint: string | null;
  toFingerprint: string | null;
  outcome: CsRotationOutcome;
  error: string | null;
  rotatedAt: string;
}

export async function listRotationEvents(
  provider?: string,
  limit = 50,
): Promise<CsCredentialRotationEvent[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (provider) params.set('provider', provider);
  const res = await apiClient.get<{ data: CsCredentialRotationEvent[] }>(
    `/api/cs/operations/credentials/events?${params.toString()}`,
  );
  return res.data.data;
}

/** Full sha256 hex digest of the NEW secret (64 lowercase hex chars). */
export const SHA256_FINGERPRINT_RE = /^[a-f0-9]{64}$/;

export interface RotateCredentialInput {
  outcome: CsRotationOutcome;
  /** sha256 hex of the NEW secret, computed by the caller — never the secret. */
  fingerprint?: string;
  reason?: string;
}

export async function rotateCredential(
  policyId: string,
  input: RotateCredentialInput,
): Promise<CsCredentialRotationEvent> {
  const res = await apiClient.post<{ data: CsCredentialRotationEvent }>(
    `/api/cs/operations/credentials/${encodeURIComponent(policyId)}/rotate`,
    input,
  );
  return res.data.data;
}

export interface UpsertCredentialPolicyInput {
  provider: string;
  rotationPolicyDays?: number;
  managedByHub?: boolean;
  lastRotatedAt?: string | null;
}

export async function upsertCredentialPolicy(
  input: UpsertCredentialPolicyInput,
): Promise<CredentialPolicyView> {
  const res = await apiClient.post<{ data: CredentialPolicyView }>('/api/cs/operations/credentials', input);
  return res.data.data;
}

// ── Security policy ─────────────────────────────────────────────────────

export type CsTwoFactorMode = 'off' | 'opt_in' | 'required';

export interface CsSecurityPolicy {
  id: string;
  organizationId: string;
  twoFactorMode: CsTwoFactorMode;
  passwordMinLength: number;
  passwordRequireComplexity: boolean;
  passwordExpiryDays: number | null;
  sessionTimeoutMinutes: number;
  ssoProvider: string;
  ipAllowlistEnabled: boolean;
  ipAllowlist: string[];
  updatedByMembershipId: string | null;
  updatedAt: string;
}

export async function getSecurityPolicy(): Promise<CsSecurityPolicy> {
  const res = await apiClient.get<{ data: CsSecurityPolicy }>('/api/cs/operations/security-policy');
  return res.data.data;
}

export interface UpdateSecurityPolicyInput {
  twoFactorMode?: CsTwoFactorMode;
  passwordMinLength?: number;
  passwordRequireComplexity?: boolean;
  passwordExpiryDays?: number | null;
  sessionTimeoutMinutes?: number;
  ssoProvider?: string;
  ipAllowlistEnabled?: boolean;
  ipAllowlist?: string[];
}

export async function updateSecurityPolicy(input: UpdateSecurityPolicyInput): Promise<CsSecurityPolicy> {
  const res = await apiClient.put<{ data: CsSecurityPolicy }>('/api/cs/operations/security-policy', input);
  return res.data.data;
}

// ── Integration errors ──────────────────────────────────────────────────

export interface CsIntegrationError {
  id: string;
  organizationId: string;
  provider: string;
  endpoint: string;
  method: string | null;
  httpStatus: number | null;
  message: string;
  requestMeta: unknown;
  triggeredByMembershipId: string | null;
  csTicketId: string | null;
  retryable: boolean;
  retriedAt: string | null;
  resolvedAt: string | null;
  occurredAt: string;
}

export interface ListIntegrationErrorsQuery {
  provider?: string;
  endpoint?: string;
  sinceHours?: number;
  unresolvedOnly?: boolean;
  page?: number;
  limit?: number;
}

export async function listIntegrationErrors(
  query: ListIntegrationErrorsQuery = {},
): Promise<{ data: CsIntegrationError[]; meta: PageMeta }> {
  const params = new URLSearchParams();
  if (query.provider) params.set('provider', query.provider);
  if (query.endpoint) params.set('endpoint', query.endpoint);
  if (query.sinceHours !== undefined) params.set('sinceHours', String(query.sinceHours));
  if (query.unresolvedOnly) params.set('unresolvedOnly', 'true');
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? 25));
  const res = await apiClient.get<{ data: CsIntegrationError[]; meta: PageMeta }>(
    `/api/cs/operations/errors?${params.toString()}`,
  );
  return res.data;
}

export async function resolveIntegrationError(id: string): Promise<CsIntegrationError> {
  const res = await apiClient.post<{ data: CsIntegrationError }>(
    `/api/cs/operations/errors/${encodeURIComponent(id)}/resolve`,
    {},
  );
  return res.data.data;
}

// ── Triage ──────────────────────────────────────────────────────────────

export interface TriageDecisionListItem extends CsTriageDecision {
  csTicket: { id: string; subject: string; status: CsTicketStatus; tier: CsTier };
}

export async function listTriageDecisions(
  query: { tier?: CsTier; sinceHours?: number; page?: number; limit?: number } = {},
): Promise<{ data: TriageDecisionListItem[]; meta: PageMeta }> {
  const params = new URLSearchParams();
  if (query.tier) params.set('tier', query.tier);
  if (query.sinceHours !== undefined) params.set('sinceHours', String(query.sinceHours));
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? 25));
  const res = await apiClient.get<{ data: TriageDecisionListItem[]; meta: PageMeta }>(
    `/api/cs/triage/decisions?${params.toString()}`,
  );
  return res.data;
}

export async function getTriageDecision(ticketId: string): Promise<CsTriageDecision> {
  const res = await apiClient.get<{ data: CsTriageDecision }>(
    `/api/cs/triage/tickets/${encodeURIComponent(ticketId)}/decision`,
  );
  return res.data.data;
}

export interface TriageBacklogResult {
  processed: number;
  skipped: number;
}

export async function runTriageBacklog(limit?: number): Promise<TriageBacklogResult> {
  const res = await apiClient.post<{ data: TriageBacklogResult }>(
    '/api/cs/triage/run-backlog',
    limit !== undefined ? { limit } : {},
  );
  return res.data.data;
}

// ── Ops agent ───────────────────────────────────────────────────────────

export type CsOpsRunStatus = 'running' | 'completed' | 'failed';

export interface OpsAgentToolCall {
  name?: string;
  input?: unknown;
  rowCount?: number | null;
  resultPreview?: string;
  error?: string | null;
}

export interface CsOpsAgentRun {
  id: string;
  organizationId: string;
  requestedByMembershipId: string;
  prompt: string;
  status: CsOpsRunStatus;
  findingsMarkdown: string | null;
  csvData: string | null;
  toolCallsJson: OpsAgentToolCall[];
  model: string;
  promptTokens: number | null;
  completionTokens: number | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

/** 202 — returns the 'running' row; poll getOpsAgentRun until it leaves 'running'. */
export async function createOpsAgentRun(question: string): Promise<CsOpsAgentRun> {
  const res = await apiClient.post<{ data: CsOpsAgentRun }>('/api/cs/ops-agent/runs', { question });
  return res.data.data;
}

export async function listOpsAgentRuns(
  page = 1,
  limit = 20,
): Promise<{ data: CsOpsAgentRun[]; meta: PageMeta }> {
  const res = await apiClient.get<{ data: CsOpsAgentRun[]; meta: PageMeta }>(
    `/api/cs/ops-agent/runs?page=${String(page)}&limit=${String(limit)}`,
  );
  return res.data;
}

export async function getOpsAgentRun(id: string): Promise<CsOpsAgentRun> {
  const res = await apiClient.get<{ data: CsOpsAgentRun }>(
    `/api/cs/ops-agent/runs/${encodeURIComponent(id)}`,
  );
  return res.data.data;
}

/** Href for the CSV download (cookie auth rides along on same-origin/api). */
export function opsAgentRunCsvUrl(id: string): string {
  const base = apiClient.defaults.baseURL ?? '';
  return `${base}/api/cs/ops-agent/runs/${encodeURIComponent(id)}/export.csv`;
}
